import React, { useContext, useState } from "react"
import { DateTime } from "luxon"
import { Button, Div, Text } from "atomize"
import { AnimatePresence, motion } from "framer-motion"
import Card from "../Card"
import { theme } from "../settings/config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faAngleDown,
	faAngleRight,
	faAngleUp,
	faUserSlash,
} from "@fortawesome/free-solid-svg-icons"
import { modalContext } from "../providers/modal.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import {
	getLessonTypeTileColors,
	getPtrLevel,
	getSatLevel,
	lessonTypeDisplayNameOf,
} from "../utils/func.utils"
import { absencesToRecover, seasonLabel } from "../utils/substitutes.utils"
import { shortPlayerName } from "../utils/fitLabels.utils"
import FittedPills from "../atoms/FittedPills"
import useSeasonAbsences, { OPEN_SPOTS_DAYS } from "../profile/useSeasonAbsences"

// Aperto o chiuso, il blocco di ogni corso, su questo telefono. Senza
// memoria si riparte da come lo si trova: aperti i corsi con qualcosa da
// recuperare.
const openKey = course => `incampo.absencesOpen.${course}`
const readOpen = (course, fallback) => {
	try {
		const stored = window.localStorage.getItem(openKey(course))
		return stored === null ? fallback : stored === "true"
	} catch {
		return fallback
	}
}
const writeOpen = (course, isOpen) => {
	try {
		window.localStorage.setItem(openKey(course), String(isOpen))
	} catch {
		// senza memoria la prossima volta si riparte da come lo si trova
	}
}

// Un posto da riempire: quando, a che livello, e chi manca. Toccandolo si
// sceglie chi viene al suo posto, come da "Aggiungi sostituto" nella
// lezione. Una riga bianca sul colore del corso, come le pastiglie sotto.
function OpenSpotRow({ lesson, absent, isFirst }) {
	const Modal = useContext(modalContext)
	const start = DateTime.fromJSDate(lesson.lessonStart.toDate()).setLocale("it")
	const satLevel = getSatLevel(lesson.satLevel)
	const ptrLevel = getPtrLevel(lesson.ptrLevel)
	const level = ptrLevel ? `Livello ${ptrLevel.label}` : satLevel?.label

	return (
		<motion.div whileTap={{ scale: 0.98 }}>
			<Div
				tag="button"
				type="button"
				w="100%"
				minH="3.25rem"
				d="flex"
				align="center"
				bg={theme.colors.surface}
				rounded="15px"
				p="0.5rem 0.75rem 0.5rem 1rem"
				m={{ t: isFirst ? "0" : "0.5rem" }}
				cursor="pointer"
				style={{ gap: "0.75rem", border: 0, font: "inherit", textAlign: "left" }}
				onClick={() => Modal.open("addSubstitute", { lessonData: lesson, absent })}>
				<Div style={{ flex: 1, minWidth: 0 }}>
					<Text textWeight="bold" textColor={theme.colors.gunmetal}>
						{start.toFormat("ccc d LLL · HH:mm")}
					</Text>
					{level && (
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{level}
						</Text>
					)}
				</Div>
				<Div textAlign="right" style={{ minWidth: 0 }}>
					<Text textWeight="bold" textColor={theme.colors.red}>
						{absent.playerName.trim()}
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						assente
					</Text>
				</Div>
				<FontAwesomeIcon icon={faAngleRight} color={theme.colors.text_light} />
			</Div>
		</motion.div>
	)
}

// Il blocco di un corso, tutto nei suoi colori come le sue lezioni: il corso
// è il titolo, e quello che c'è dentro gli appartiene. In cima una riga che
// lo apre e lo chiude, col nome del
// corso nei suoi colori e quante ne restano; sotto, chi ha lezioni da
// recuperare, con quante e prima chi ne ha di più, e chi le ha recuperate.
// Pastiglie corte, che riempiono la riga: il dettaglio si apre toccando.
// Sopra a tutto, i posti da riempire nei prossimi giorni: accanto a chi ha
// un recupero dello stesso corso, che è chi li riempie.
function CourseBlock({ course, name, colors, entries, spots = [], isFirst, groupTitle, pills }) {
	const toRecover = entries
		.filter(entry => absencesToRecover(entry) > 0)
		.sort((a, b) => absencesToRecover(b) - absencesToRecover(a))
	const recovered = entries.filter(entry => absencesToRecover(entry) === 0)
	const [isOpen, setOpen] = useState(() =>
		readOpen(course, toRecover.length > 0 || spots.length > 0),
	)
	const toggle = () => {
		writeOpen(course, !isOpen)
		setOpen(!isOpen)
	}

	const left = toRecover.reduce((sum, entry) => sum + absencesToRecover(entry), 0)
	// recuperate qui o con un altro maestro: per il corso è lo stesso
	const done = entries.reduce(
		(sum, entry) =>
			sum +
			Math.min(
				entry.absences.length,
				entry.recoveries.length + (entry.elsewhere?.length ?? 0),
			),
		0,
	)
	// con dei posti da riempire, le recuperate restano dentro: la riga dice
	// cosa c'è da fare, e su una riga sola
	const summary =
		[
			spots.length > 0 &&
				`${spots.length} ${spots.length === 1 ? "posto libero" : "posti liberi"}`,
			left > 0 && `${left} da recuperare`,
			spots.length === 0 && done > 0 && `${done} ${done === 1 ? "recuperata" : "recuperate"}`,
		]
			.filter(Boolean)
			.join(" · ") || "niente da recuperare"

	return (
		<Div bg={colors.bg} rounded="15px" p={{ x: "1rem" }} m={{ t: isFirst ? "0" : "0.75rem" }}>
			<Div
				tag="button"
				type="button"
				aria-expanded={isOpen}
				w="100%"
				minH="3.5rem"
				d="flex"
				align="center"
				bg="transparent"
				cursor="pointer"
				p={{ t: "0.875rem", b: "0.5rem", x: "0" }}
				style={{ gap: "0.6rem", border: 0, font: "inherit", textAlign: "left" }}
				onClick={toggle}>
				<Div tag="span" style={{ flex: 1, minWidth: 0 }}>
					<Text
						tag="span"
						d="block"
						textSize="subheader"
						textWeight="bold"
						textColor={theme.colors.gunmetal}>
						{name}
					</Text>
					<Text tag="span" d="block" textSize="caption" textColor={theme.colors.gunmetal}>
						{summary}
					</Text>
				</Div>
				<FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} color={theme.colors.gunmetal} />
			</Div>
			<AnimatePresence initial={false}>
				{isOpen && (
					<motion.section
						style={{ overflow: "hidden" }}
						initial="collapsed"
						animate="open"
						exit="collapsed"
						variants={{
							open: { opacity: 1, height: "auto" },
							collapsed: { opacity: 0, height: 0 },
						}}
						transition={{ duration: 0.25, ease: [0.04, 0.62, 0.23, 0.98] }}>
						<Div p={{ t: "0.25rem", b: "1rem" }}>
							{spots.length > 0 && (
								<Div m={{ b: entries.length > 0 ? "1rem" : "0" }}>
									{groupTitle(`Posti liberi nei prossimi ${OPEN_SPOTS_DAYS} giorni`)}
									{spots.map((spot, i) => (
										<OpenSpotRow
											key={`${spot.lesson.lessonID}-${spot.absent.playerID || spot.absent.playerName}`}
											{...spot}
											isFirst={i === 0}
										/>
									))}
								</Div>
							)}
							{toRecover.length > 0 && (
								<Div m={{ b: recovered.length > 0 ? "1rem" : "0" }}>
									{groupTitle("Da recuperare")}
									{pills(toRecover, true)}
								</Div>
							)}
							{recovered.length > 0 && (
								<Div>
									{groupTitle("Recuperate")}
									{pills(recovered, false)}
								</Div>
							)}
						</Div>
					</motion.section>
				)}
			</AnimatePresence>
		</Div>
	)
}

const AbsencesCard = () => {
	const Modal = useContext(modalContext)
	const { getLessonType } = useContext(sharedDataContext)
	// La stagione, non il mese che si sta guardando: un'assenza di settembre
	// resta da recuperare anche a novembre.
	const { byCourse, spotsByCourse, season, isLoaded } = useSeasonAbsences()

	// Un blocco per corso, perché un CAD saltato si recupera solo in un CAD e
	// un SAT in un SAT: messi insieme, i conti non dicevano dove recuperare.
	// Sempre nello stesso ordine, per nome, così si ritrovano.
	const courses = Object.entries(byCourse)
		.map(([course, absences]) => ({
			course,
			name: lessonTypeDisplayNameOf(getLessonType(course), course.toUpperCase()),
			// i colori del corso, come nelle sue lezioni
			colors: getLessonTypeTileColors(getLessonType(course)),
			entries: Object.values(absences),
			spots: spotsByCourse[course] ?? [],
		}))
		.filter(({ entries }) => entries.length > 0)
		.sort((a, b) => a.name.localeCompare(b.name, "it"))

	// sottovoce: il titolo è il corso
	const groupTitle = title => (
		<Text textSize="caption" textColor={theme.colors.gunmetal} m={{ b: "0.4rem" }}>
			{title}
		</Text>
	)

	const pill = (entry, isToRecover, label, periodText) => (
		<Button
			h="auto"
			d="flex"
			align="center"
			p={isToRecover ? "0.4rem 0.4rem 0.4rem 0.75rem" : "0.4rem 0.75rem"}
			rounded="12px"
			// bianche sul colore del corso: di rosso resta il numero, quello che
			// c'è da recuperare
			bg={theme.colors.surface}
			textColor={theme.colors.gunmetal}
			textSize="body"
			textWeight={isToRecover ? "bold" : "normal"}
			style={{ gap: "0.5rem", whiteSpace: "nowrap" }}
			onClick={() => Modal.open("playerAbsences", { ...entry, periodText })}>
			{label}
			{isToRecover && (
				<Text
					tag="span"
					textSize="caption"
					textWeight="bold"
					textColor={theme.colors.surface}
					bg={theme.colors.red}
					rounded="circle"
					p={{ x: "0.4rem" }}
					style={{ minWidth: "1.25rem", textAlign: "center" }}>
					{absencesToRecover(entry)}
				</Text>
			)}
		</Button>
	)

	// I nomi si accorciano — "Fab. Arvieri" — solo quando serve perché la
	// riga non vada a capo lasciando vuoto lo spazio accanto.
	const pills = (group, isToRecover, periodText) => (
		<FittedPills
			items={group.map(entry => ({
				key: entry.playerID || entry.playerName,
				full: entry.playerName,
				short: shortPlayerName(entry.playerName),
				entry,
			}))}
			renderPill={(item, label) => pill(item.entry, isToRecover, label, periodText)}
		/>
	)

		return (
		<Card bg={theme.colors.surface}>
			<Div d="flex" align="center" style={{ gap: "0.5rem" }} m={{ b: "0.5rem" }}>
				<Div
					w="40px"
					h="40px"
					rounded="circle"
					bg={theme.colors.red_light}
					d="flex"
					align="center"
					justify="center">
					<FontAwesomeIcon icon={faUserSlash} color={theme.colors.red} />
				</Div>
				<Div>
					<Text textWeight="bold" textSize="title">
						Assenze abbonati
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{seasonLabel(season)}
					</Text>
				</Div>
			</Div>

			{courses.length > 0 ? (
				courses.map((course, i) => (
					<CourseBlock
						key={course.course}
						{...course}
						isFirst={i === 0}
						groupTitle={groupTitle}
						pills={(group, isToRecover) =>
							pills(group, isToRecover, `${course.name} · ${seasonLabel(season)}`)
						}
					/>
				))
			) : (
				// finché la stagione non è letta la riga resta vuota, della sua
				// altezza: "nessuna assenza" sarebbe falso, e poi la scheda si
				// allungherebbe di colpo
				<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
					<Text
						textSize="subheader"
						textColor={theme.colors.text_light}
						style={{ visibility: isLoaded ? "visible" : "hidden" }}>
						Nessuna assenza! 🎉
					</Text>
				</Div>
			)}
		</Card>
	)
}

export default AbsencesCard
